import { Link } from 'react-router';
import { correlationLink } from '../lib/opsQuery';
import { CopyText } from './States';

export const shortId = (id: string, n = 8): string => (id.length > n + 2 ? `${id.slice(0, n)}…` : id);

/** A shortened, copyable ID linking somewhere. */
export function IdLink({ id, to, n = 8, label }: { id: string; to: string; n?: number; label?: string }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 text-primary hover:underline">
      {label ? <span>{label}</span> : null}
      <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs" title={id}>
        {shortId(id, n)}
      </code>
    </Link>
  );
}

/** Correlation ID -> Correlation page (spec §116). */
export function CorrelationLink({ id, n = 8, label }: { id: string | null | undefined; n?: number; label?: string }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return <IdLink id={id} to={correlationLink(id)} n={n} label={label} />;
}

export function TraceLink({ id, n = 10, label }: { id: string | null | undefined; n?: number; label?: string }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return <IdLink id={id} to={`/ops/traces/${encodeURIComponent(id)}`} n={n} label={label} />;
}

export function RegistryTxLink({ id, label, n = 8 }: { id: string | null | undefined; label?: string; n?: number }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return <IdLink id={id} to={`/registry/transactions/${encodeURIComponent(id)}`} n={n} label={label} />;
}

export function WorkflowLink({ id, label, n = 8 }: { id: string | null | undefined; label?: string; n?: number }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return <IdLink id={id} to={`/ops/workflows/${encodeURIComponent(id)}`} n={n} label={label} />;
}

/** Domain by UUID or name; the detail page canonicalises to the A-label. */
export function DomainLink({ keyOrName, label }: { keyOrName: string | null | undefined; label?: string }) {
  if (!keyOrName) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={`/domains/${encodeURIComponent(keyOrName)}`} className="font-medium text-primary hover:underline">
      {label ?? keyOrName}
    </Link>
  );
}

/** A copyable ID without a destination. */
export function PlainId({ id, n = 8 }: { id: string | null | undefined; n?: number }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return <CopyText value={id} display={shortId(id, n)} />;
}
